/**
 * WorksheetSetup.jsx - the workbook's "1. Project Info" pricing inputs: the
 * rate-card customer (PO Customer), the start date and how many days the
 * ledger runs, the per-day expense costs (billable and not), and the
 * engineers (the BillRateTable): a named person from the staff table or a
 * skill level with nobody named, their round-trip flight, and - Quotes Edit
 * only - a bill rate typed over the card. The rates, sell and cost shown come
 * from the server's calc; cost only for the 'cost-rates' area.
 */
import { Btn, Field, Grid, MUTED, NumInput, Section, SelectInput, TextInput, fmtMoney, fmtNum, td, th } from '../quotes/quoteUi';
import { LEVELS, MAX_WS_ENGINEERS, PER_DAY, blankEngineer, updateEngineer } from '../quotes/worksheetDraft';

const whoValue = (e) => (e.name ? `staff:${e.name}` : e.level ? `level:${e.level}` : '');

export default function WorksheetSetup({ ws, setWs, settings, staff = [], calc, seesCosts, canOverride, readOnly, active, setActive }) {
  const ro = !!readOnly;
  const engineers = ws.engineers || [];
  const calcEng = (calc && calc.engineers) || [];
  const billDefaults = (settings.worksheet && settings.worksheet.billableDefaults) || {};
  const nbDefaults = (settings.worksheet && settings.worksheet.nonBillableDefaults) || {};
  const locFlight = (loc) => {
    const l = (settings.empLocations || []).find((x) => x.name === loc);
    return l && Number(l.flight) ? String(l.flight) : '';
  };
  const setPer = (side, k) => (v) => setWs((w) => ({ ...w, [side]: { ...(w[side] || {}), [k]: v } }));
  const pickWho = (i, v) =>
    setWs((w) =>
      updateEngineer(w, i, (e) => {
        if (v.startsWith('staff:')) {
          const name = v.slice(6);
          const s = staff.find((x) => x.name === name) || {};
          return { ...e, name, level: s.skillLevel || e.level || '', flights: e.flights || locFlight(s.location) };
        }
        if (v.startsWith('level:')) return { ...e, name: '', level: v.slice(6) };
        return { ...e, name: '', level: '' };
      })
    );
  const staffNames = new Set(staff.map((s) => s.name));
  const customers = (settings.customerRates || []).map((c) => c.customer);

  return (
    <>
      <Section
        title="1  Rates and expenses"
        hint="The customer's rate card prices every engineer by skill level. Per-day expenses count on each day flagged Billable / NonBillable; a Travel day adds half the engineer's round-trip flight."
      >
        <Grid cols={4}>
          <Field label="Rate card customer (PO customer)">
            <SelectInput
              aria-label="Rate card customer"
              value={ws.customer}
              blank="Pick one..."
              options={customers}
              disabled={ro}
              onChange={(v) => setWs((w) => ({ ...w, customer: v }))}
            />
          </Field>
          <Field label="Start date">
            <TextInput
              type="date"
              aria-label="Start date"
              value={ws.start}
              disabled={ro}
              onChange={(v) => setWs((w) => ({ ...w, start: v }))}
            />
          </Field>
          <Field label="Days on the sheet" title="How many days the daily ledger shows (the workbook had 161)">
            <NumInput aria-label="Days on the sheet" value={ws.days} disabled={ro} onChange={(v) => setWs((w) => ({ ...w, days: v }))} />
          </Field>
          <div />
          {PER_DAY.map(([k, label]) => (
            <Field key={`b${k}`} label={`Billable ${label} / day`}>
              <NumInput
                aria-label={`Billable ${label} per day`}
                value={(ws.billable || {})[k] ?? ''}
                placeholder={billDefaults[k] !== undefined ? String(billDefaults[k]) : 'card default'}
                disabled={ro}
                onChange={setPer('billable', k)}
              />
            </Field>
          ))}
          {PER_DAY.map(([k, label]) => (
            <Field key={`n${k}`} label={`Non-billable ${label} / day`}>
              <NumInput
                aria-label={`Non-billable ${label} per day`}
                value={(ws.nonBillable || {})[k] ?? ''}
                placeholder={nbDefaults[k] !== undefined ? String(nbDefaults[k]) : 'card default'}
                disabled={ro}
                onChange={setPer('nonBillable', k)}
              />
            </Field>
          ))}
        </Grid>
      </Section>

      <Section
        title="Engineers"
        hint="One column of the WorkSheet each. Name someone from the staff table, or pick a skill level when nobody is named yet (costed at that level's average)."
        right={
          !ro && (
            <Btn
              small
              disabled={engineers.length >= MAX_WS_ENGINEERS}
              onClick={() => setWs((w) => ({ ...w, engineers: [...(w.engineers || []), blankEngineer()] }))}
            >
              + Add engineer
            </Btn>
          )
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[
                '#',
                'Engineer',
                'Level',
                'Location',
                ...(canOverride ? ['Bill rate', 'Rate over'] : []),
                'Flight (round trip)',
                'Hours',
                'Sell',
                ...(seesCosts ? ['Burden', 'Cost'] : []),
                '',
              ].map((x) => (
                <th key={x} style={th}>
                  {x}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {engineers.map((e, i) => {
              const c = calcEng[i];
              return (
                <tr
                  key={i}
                  style={{ background: active === i ? '#F1F8E9' : undefined, cursor: setActive ? 'pointer' : undefined }}
                  onClick={() => setActive && setActive(i)}
                >
                  <td style={{ ...td, width: 24, fontSize: 11, color: MUTED }}>{i + 1}</td>
                  <td style={{ ...td, minWidth: 190 }}>
                    <select
                      aria-label={`Engineer ${i + 1}`}
                      value={whoValue(e)}
                      disabled={ro}
                      onChange={(ev) => pickWho(i, ev.target.value)}
                      style={{
                        width: '100%',
                        fontSize: 12,
                        padding: '5px 6px',
                        borderRadius: 6,
                        border: '1px solid #E5E9DD',
                        fontFamily: 'Montserrat',
                      }}
                    >
                      <option value="">Pick someone...</option>
                      <optgroup label="Nobody named yet">
                        {LEVELS.map((L) => (
                          <option key={L} value={`level:${L}`}>{`A ${L} engineer`}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Staff">
                        {e.name && !staffNames.has(e.name) && <option value={`staff:${e.name}`}>{e.name} (not in the staff table)</option>}
                        {staff.map((s) => (
                          <option
                            key={s.name}
                            value={`staff:${s.name}`}
                          >{`${s.name} - ${s.skillLevel || '?'} ${s.skillType || ''}${s.location ? ` - ${s.location}` : ''}`}</option>
                        ))}
                      </optgroup>
                    </select>
                  </td>
                  <td style={{ ...td, fontSize: 11.5, width: 76 }}>{(c && c.level) || e.level || '-'}</td>
                  <td style={{ ...td, fontSize: 11.5, width: 90 }}>{(c && c.location) || ''}</td>
                  {canOverride && <td style={{ ...td, fontSize: 11.5, textAlign: 'right', width: 70 }}>{c ? fmtMoney(c.billRate) : ''}</td>}
                  {canOverride && (
                    <td style={{ ...td, width: 76 }}>
                      <NumInput
                        aria-label={`Engineer ${i + 1} bill rate override`}
                        value={e.rate}
                        placeholder="card"
                        disabled={ro}
                        onChange={(v) => setWs((w) => updateEngineer(w, i, (x) => ({ ...x, rate: v })))}
                      />
                    </td>
                  )}
                  <td style={{ ...td, width: 100 }}>
                    <NumInput
                      aria-label={`Engineer ${i + 1} flight`}
                      value={e.flights}
                      placeholder="0"
                      disabled={ro}
                      onChange={(v) => setWs((w) => updateEngineer(w, i, (x) => ({ ...x, flights: v })))}
                    />
                  </td>
                  <td style={{ ...td, fontSize: 11.5, textAlign: 'right', width: 56 }}>{c ? fmtNum(c.hours) : ''}</td>
                  <td style={{ ...td, fontSize: 11.5, textAlign: 'right', fontWeight: 700, width: 92 }}>{c ? fmtMoney(c.sell) : ''}</td>
                  {seesCosts && (
                    <td style={{ ...td, fontSize: 11.5, textAlign: 'right', width: 70 }}>{c && c.costKnown ? fmtMoney(c.burden) : '-'}</td>
                  )}
                  {seesCosts && <td style={{ ...td, fontSize: 11.5, textAlign: 'right', width: 92 }}>{c ? fmtMoney(c.cost) : ''}</td>}
                  <td style={{ ...td, width: 30 }}>
                    {!ro && (
                      <Btn
                        small
                        danger
                        aria-label={`Remove engineer ${i + 1}`}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          if (
                            Object.keys(e.rows || {}).length &&
                            !window.confirm(`Remove ${e.name || `engineer ${i + 1}`} and their days?`)
                          )
                            return;
                          setWs((w) => ({ ...w, engineers: w.engineers.filter((_, j) => j !== i) }));
                          if (setActive) setActive(0);
                        }}
                      >
                        x
                      </Btn>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>
    </>
  );
}
